import { useEffect, useRef, useState, useCallback } from 'react';

let apiPromise;
const loadApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(window.YT); };
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    });
  }
  return apiPromise;
};

// YouTube player bound to a [start, end] segment, with a live clock for transcript sync.
export const useYouTubeClip = ({ active, videoId, start = 0, end, onEnded }) => {
  const host = useRef(null);
  const player = useRef(null);
  const ready = useRef(false);
  const pending = useRef(false);
  const done = useRef(false);
  const endedCb = useRef(onEnded);
  const [time, setTime] = useState(start);
  const [playing, setPlaying] = useState(false);
  endedCb.current = onEnded;

  const finish = () => {
    if (done.current) return;
    done.current = true;
    setPlaying(false);
    endedCb.current?.();
  };

  const seekStart = () => {
    done.current = false;
    player.current.seekTo(start, true);
    player.current.playVideo();
    setTime(start);
  };

  useEffect(() => {
    if (!active || !host.current) return undefined;
    let cancelled = false;
    loadApi().then((YT) => {
      if (cancelled) return;
      player.current = new YT.Player(host.current, {
        videoId, host: 'https://www.youtube-nocookie.com',
        playerVars: { autoplay: 1, rel: 0, start, playsinline: 1, modestbranding: 1, origin: window.location.origin },
        events: {
          onReady: () => { ready.current = true; if (pending.current) { pending.current = false; seekStart(); } },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) setPlaying(true);
            else if (e.data === YT.PlayerState.ENDED) finish();
            else setPlaying(false);
          },
        },
      });
    });
    return () => { cancelled = true; ready.current = false; player.current?.destroy?.(); player.current = null; };
  }, [active, videoId, start]);

  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      const t = player.current?.getCurrentTime?.() ?? 0;
      setTime(t);
      if (end && t >= end) { player.current.pauseVideo(); finish(); }
    }, 100);
    return () => clearInterval(id);
  }, [playing, end]);

  const replay = useCallback(() => {
    if (ready.current) seekStart(); else pending.current = true;
  }, [start]);

  return { host, time, playing, replay };
};
